import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getDashboardDailyStatisticsApi } from '~/features/dashboard/api/get-dashboard-daily-statistics.api';
import { getDashboardHomeApi } from '~/features/dashboard/api/get-dashboard-home.api';
import { getDashboardRealtimeActivitiesApi } from '~/features/dashboard/api/get-dashboard-realtime-activities.api';
import { getDashboardStoreStatisticsApi } from '~/features/dashboard/api/get-dashboard-store-statistics.api';
import { getDashboardSummaryApi } from '~/features/dashboard/api/get-dashboard-summary.api';

import type {
  DailyStatistics,
  DashboardHome,
  DashboardStatisticsParams,
  DashboardSummary,
  Inquiry,
  RealtimeActivity,
  StoreStatistics,
  SystemAlert,
} from '~/features/dashboard/types/dashboard.type';

export interface DashboardState {
  isLoading: boolean;
  summary: DashboardSummary | null;
  dailyStatistics: DailyStatistics[];
  storeStatistics: StoreStatistics[];
  realtimeActivities: RealtimeActivity[];
  systemAlerts: SystemAlert[];
  inquiries: Inquiry[];
}

const initialState: DashboardState = {
  isLoading: false,
  summary: null,
  dailyStatistics: [],
  storeStatistics: [],
  realtimeActivities: [],
  systemAlerts: [],
  inquiries: [],
};

/**
 * 대시보드 요약 조회
 */
export const fetchDashboardSummary = createAsyncThunk<DashboardSummary>(
  'dashboard/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getDashboardSummaryApi();
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 일별 통계 조회
 */
export const fetchDashboardDailyStatistics = createAsyncThunk<
  DailyStatistics[],
  DashboardStatisticsParams | undefined
>('dashboard/fetchDailyStatistics', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getDashboardDailyStatisticsApi(params);
    return data.data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 매장별 통계 조회
 */
export const fetchDashboardStoreStatistics = createAsyncThunk<StoreStatistics[]>(
  'dashboard/fetchStoreStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getDashboardStoreStatisticsApi();
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 대시보드 홈 통합 조회
 */
export const fetchDashboardHome = createAsyncThunk<DashboardHome>(
  'dashboard/fetchHome',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getDashboardHomeApi();
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 실시간 활동 로그 조회
 */
export const fetchDashboardRealtimeActivities = createAsyncThunk<
  RealtimeActivity[],
  number | undefined
>('dashboard/fetchRealtimeActivities', async (limit, { rejectWithValue }) => {
  try {
    const { data } = await getDashboardRealtimeActivitiesApi(limit);
    return data.data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

const DashboardSlice = createSlice({
  initialState,
  name: 'dashboard',
  reducers: {
    resetDashboard: state => {
      state.summary = null;
      state.dailyStatistics = [];
      state.storeStatistics = [];
      state.realtimeActivities = [];
      state.systemAlerts = [];
      state.inquiries = [];
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchDashboardSummary
      .addCase(fetchDashboardSummary.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchDashboardSummary.rejected, state => {
        state.isLoading = false;
      })
      // fetchDashboardDailyStatistics
      .addCase(fetchDashboardDailyStatistics.fulfilled, (state, action) => {
        state.dailyStatistics = action.payload;
      })
      // fetchDashboardStoreStatistics
      .addCase(fetchDashboardStoreStatistics.fulfilled, (state, action) => {
        state.storeStatistics = action.payload;
      })
      // fetchDashboardRealtimeActivities
      .addCase(fetchDashboardRealtimeActivities.fulfilled, (state, action) => {
        state.realtimeActivities = action.payload;
      })
      // fetchDashboardHome
      .addCase(fetchDashboardHome.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchDashboardHome.fulfilled, (state, action) => {
        state.summary = action.payload.summary;
        state.realtimeActivities = action.payload.realtimeActivities;
        state.systemAlerts = action.payload.systemAlerts;
        state.inquiries = action.payload.inquiries;
        state.isLoading = false;
      })
      .addCase(fetchDashboardHome.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { resetDashboard } = DashboardSlice.actions;
export default DashboardSlice;
